'use strict';

var dateFns = require('date-fns');
var React = require('react');
var reactUtils = require('@sravni/react-utils');
var zIndexes = require('../../../constants/zIndexes.js');
var Calendar = require('../../Calendar/src/Calendar.js');
var date = require('../../Calendar/utils/date.js');
var Portal = require('../../Portal/src/Portal.js');
var TextInput = require('../../TextInput/src/TextInput.js');
var Fade = require('../../Transition/src/Fade.js');
require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
var DateInput_module = require('../styles/DateInput.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const isDate = (value) => dateFns.isValid(value);
const isDateRange = (value) => !!value && 'from' in value && 'to' in value;
/**
 * @description To select or input a date
 *
 * @component
 * @example
 * ```jsx
 * <DateInput />
 * ```
 */
const DateInput = React__namespace.forwardRef((props, ref) => {
    const { defaultValue, value, autoFocus, mode, defaultDate, maxDate, minDate, disabledIntervals, rangeLimit, dateFormat, invalid, disabled, readOnly, placement, className, onClick, onChange, onFocus, onBlur, ...restProps } = props;
    const cx = reactUtils.useStyles(DateInput_module.default);
    const classNames = cx(className, 'container');
    const calendarWrapper = cx('calendar-wrapper');
    const reference = React__namespace.useRef(null);
    const inputRef = React__namespace.useRef(null);
    const calendarRef = React__namespace.useRef(null);
    const popper = React__namespace.useRef(null);
    const isFirstRender = React__namespace.useRef(true);
    const [visible, setVisible] = reactUtils.useBoolean(false);
    const [focused, setFocused] = reactUtils.useBoolean(false);
    const { getReferenceProps, getPopperProps } = reactUtils.usePositioner({
        placement,
        offset: [0, 8],
    });
    const [innerValue, setInnerValue] = React__namespace.useState(value || defaultValue);
    const formControlContext = reactUtils.useFormControlContext();
    const displayValue = React__namespace.useMemo(() => {
        if (mode === 'range') {
            return isDateRange(innerValue)
                ? `${date.getDateFormatted(innerValue.from, dateFormat?.from)} — ${date.getDateFormatted(innerValue.to, dateFormat?.to)}`
                : undefined;
        }
        if (mode === 'single') {
            return isDate(innerValue) ? date.getDateFormatted(innerValue, dateFormat) : undefined;
        }
        return undefined;
    }, [innerValue, dateFormat, mode]);
    const handleChangeSelected = React__namespace.useCallback((range) => {
        onChange?.(range);
        setInnerValue(range);
        setVisible.off();
    }, [onChange, setVisible]);
    const handleClick = React__namespace.useCallback((event) => {
        if (disabled)
            return;
        !readOnly && setVisible.toggle();
        onClick?.(event);
    }, [disabled, readOnly, setVisible, onClick]);
    const handleFocus = React__namespace.useCallback((event) => {
        if (!focused) {
            setFocused.on();
            onFocus?.(event);
        }
    }, [focused, onFocus, setFocused]);
    const handleBlur = React__namespace.useCallback((event) => {
        if (event.relatedTarget === calendarRef.current || calendarRef.current?.contains(event.relatedTarget))
            return;
        setVisible.off();
        onBlur?.(event);
    }, [onBlur, setVisible]);
    const calendar = React__namespace.useMemo(() => (
    // @ts-ignore
    React__namespace.createElement(Calendar.Calendar, { ref: calendarRef, mode: mode, maxDate: maxDate, minDate: minDate, rangeLimit: rangeLimit, disabledIntervals: disabledIntervals, selection: innerValue, defaultDate: defaultDate, tabIndex: -1, onChange: handleChangeSelected })), [defaultDate, mode, minDate, maxDate, rangeLimit, disabledIntervals, innerValue, handleChangeSelected]);
    const view = React__namespace.useMemo(() => (React__namespace.createElement(Portal.Portal, null,
        React__namespace.createElement(Fade.Fade, { visible: visible, unmountOnExit: true },
            React__namespace.createElement("div", { className: calendarWrapper, ...getPopperProps({ style: { zIndex: zIndexes.Z_INDEXES.DROPDOWN } }, popper) }, calendar)))), [visible, calendarWrapper, getPopperProps, calendar]);
    reactUtils.useKeyPress({
        enabled: visible || focused,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                setVisible.off();
                inputRef.current?.focus();
            },
            Tab: () => {
                setVisible.off();
                setFocused.off();
            },
        },
    }, [visible, focused]);
    React__namespace.useEffect(() => {
        if (!isFirstRender.current) {
            setInnerValue(value);
        }
        else {
            isFirstRender.current = false;
        }
    }, [value]);
    React__namespace.useEffect(() => {
        autoFocus && inputRef.current && inputRef.current.focus();
    }, [autoFocus]);
    React__namespace.useEffect(() => {
        const listener = (event) => {
            if (reference.current?.contains(event.target)) {
                return;
            }
            if (popper.current?.contains(event.target)) {
                return;
            }
            setVisible.off();
            setFocused.off();
        };
        document.addEventListener('mousedown', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
        };
    }, [reference, popper, setVisible, setFocused]);
    return (React__namespace.createElement("div", { "data-qa": "DateInput", className: classNames },
        React__namespace.createElement("div", { ...getReferenceProps({}, reference), onClick: handleClick },
            React__namespace.createElement(TextInput.TextInput, { ...restProps, ref: reactUtils.useMergedRef(ref, inputRef), value: displayValue, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, readOnly: true, onFocus: handleFocus, onBlur: handleBlur })),
        view));
});
DateInput.defaultProps = {
    mode: 'single',
    placement: 'auto-start',
};
DateInput.displayName = 'DateInput';

exports.DateInput = DateInput;
//# sourceMappingURL=DateInput.js.map
